import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardLabel } from '@/components/ui/card';
import { getAssetUrl } from '@/lib/constants';

const memberAvatar = (memberId: string) =>
  getAssetUrl(`organizations/DEMO_ORG_GREEN_VALLEY/members/${memberId}/avatar/seed.jpg`);

/**
 * Seeded Green Valley staff. Emails + password match
 * `apps/api/src/scripts/seed-demo-review-data.ts` (`DEMO_STAFF_PASSWORD`).
 * Avatars are the seed photos already on those member profiles.
 * Crew roles have no office Cognito user — they sign in on the crew app.
 * Rendered only behind `showDemoLogins` so this never appears in production.
 */
export const GREEN_VALLEY_STAFF = [
  {
    name: 'Maria Green',
    role: 'Owner',
    email: 'maria@greenvalley.example',
    password: 'GreenValley!Demo1',
    initials: 'MG',
    avatarUrl: memberAvatar('DEMO_MEMBER_OWNER'),
  },
  {
    name: 'James Wilson',
    role: 'Admin',
    email: 'james@greenvalley.example',
    password: 'GreenValley!Demo1',
    initials: 'JW',
    avatarUrl: memberAvatar('DEMO_MEMBER_ADMIN'),
  },
] as const;

export type GreenValleyStaff = (typeof GREEN_VALLEY_STAFF)[number];

interface GreenValleyQuickLoginProps {
  loading?: boolean;
  onSelect: (staff: GreenValleyStaff) => void;
}

export function GreenValleyQuickLogin({ loading, onSelect }: GreenValleyQuickLoginProps) {
  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="px-6 pt-5 pb-1">
        <CardLabel>Green Valley</CardLabel>
      </div>
      <CardContent className="pt-2">
        <div className="flex justify-center gap-1">
          {GREEN_VALLEY_STAFF.map((staff) => (
            <Button
              key={staff.email}
              type="button"
              variant="ghost"
              disabled={loading}
              onClick={() => onSelect(staff)}
              aria-label={`Sign in as ${staff.name}, ${staff.role}`}
              className="h-auto flex-col gap-2 px-6 py-3"
            >
              <Avatar className="h-14 w-14">
                <AvatarImage src={staff.avatarUrl} alt={staff.name} />
                <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                  {staff.initials}
                </AvatarFallback>
              </Avatar>
              <span className="flex flex-col items-center leading-tight">
                <span className="text-sm font-medium">{staff.name}</span>
                <span className="text-xs font-medium text-muted-foreground">{staff.role}</span>
              </span>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
